import React, { forwardRef, useRef } from 'react';
import classNames from 'classnames';

import { AsteriskIcon } from '../../icons';
import OverlayTrigger from '../OverlayTrigger';
import Spinner from '../Spinner';
import Tooltip from '../Tooltip';
import { type TooltipProps } from '../Tooltip';

import styles from './_index.module.scss';

interface ButtonV2Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  disabled?: boolean;
  icon?: React.ReactNode;
  isLoading?: boolean;
  isRequired?: boolean;
  minWidth?: boolean;
  shape?: 'round' | 'square';
  size?: 'small' | 'medium' | 'large';
  tooltip?: TooltipProps['children'];
  tooltipProps?: TooltipProps;
  variant?: 'default' | 'error' | 'primary' | 'subtle';
}

const ButtonV2 = (
  {
    children,
    className,
    disabled,
    icon,
    isLoading,
    isRequired = false,
    minWidth,
    onClick,
    shape = 'square',
    size = 'medium',
    tooltip,
    tooltipProps,
    variant = 'default',
    ...props
  }: ButtonV2Props,
  parentRef?: React.ForwardedRef<HTMLButtonElement>
) => {
  const localRef = useRef(null);
  const ref = parentRef || localRef;

  const buttonStyles = [
    styles[`button-${variant}`],
    styles[`button-shape-${shape}`],
    styles[`button-size-${size}`],
    { [styles['button-min-width']]: minWidth },
    { [styles['is-loading']]: isLoading },
  ];

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isLoading) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <OverlayTrigger.Tooltip
      overlay={disabled && tooltip ? <Tooltip {...tooltipProps}>{tooltip}</Tooltip> : <></>}
      placement={tooltipProps?.placement}
    >
      <button
        className={classNames(...buttonStyles, className)}
        disabled={disabled}
        onClick={handleClick}
        ref={ref}
        {...props}
      >
        {icon && <span className={styles['button-icon']}>{icon}</span>}
        <span className={styles['button-children']}>{children}</span>
        {isRequired && (
          <span className={styles['button-required-icon']}>
            <AsteriskIcon />
          </span>
        )}
        <Spinner className={styles['button-spinner']} />
      </button>
    </OverlayTrigger.Tooltip>
  );
};

const ButtonV2WithRef = forwardRef(ButtonV2);

export default ButtonV2WithRef;
